import React, { useEffect, useState, useCallback } from 'react';
import './RechargeAllowanceSettings.css';
import Spinner from './Spinner';

/**
 * Personal rest day allowance, usable outside of dojos.
 *
 * Lets the user pick how many rest days they grant themselves per year and
 * shows how many remain. Rest days are consumed from the workout activity
 * card in the activity feed (see Workout.js).
 */
const RestDaysSettings = ({ backendService, dojoContext = false }) => {
  const [status, setStatus] = useState(null); // { total, used, remaining, days }
  const [loading, setLoading] = useState(true);
  const [draftQuota, setDraftQuota] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await backendService.getRestDays();
      setStatus(data);
      setDraftQuota(String(data?.total ?? 0));
    } catch (err) {
      setError('Could not load rest day settings.');
    } finally {
      setLoading(false);
    }
  }, [backendService]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Refresh when a workout elsewhere consumes/releases a rest day.
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener('regimen:restDays:changed', handler);
    return () => window.removeEventListener('regimen:restDays:changed', handler);
  }, [refresh]);

  const save = async () => {
    if (status?.can_edit === false) {
      setError('Only the dojo chief can change this allowance.');
      setEditing(false);
      return;
    }
    const value = parseInt(draftQuota, 10);
    if (!Number.isFinite(value) || value < 0) {
      setError('Enter a non-negative whole number.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const data = await backendService.updateRestDayQuota(value);
      setStatus(data);
      setDraftQuota(String(data.total));
      setEditing(false);
    } catch (err) {
      setError('Could not save rest day quota.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rest-days-section">
        <h4 className="rest-days-title"><span aria-hidden="true">🌴</span> {dojoContext ? 'Your Rest Days' : 'Rest Days'}</h4>
        <div className="rest-days-loading" role="status" aria-live="polite">
          <Spinner />
          <span>Loading rest days…</span>
        </div>
      </div>
    );
  }

  const total = status?.total ?? 0;
  const used = status?.used ?? 0;
  const isDojoOwned = status?.scope === 'dojo';
  const canEditAllowance = status?.can_edit !== false;
  const allowanceOwner = status?.owner_name || 'your dojo';
  const allowanceEnabled = status?.enabled !== false;
  const remaining = status?.remaining ?? Math.max(0, total - used);
  const recoveryProgress = Math.max(0, Math.min(1, Number(status?.recovery_progress) || 0));
  const refillDaysPerToken = Number(status?.refill_days_per_token) || (total > 0 ? 365.25 / total : 0);
  const refillCadenceDays = refillDaysPerToken > 0 ? Math.max(1, Math.round(refillDaysPerToken)) : 0;
  const recoveryPercent = remaining < total
    ? Math.min(99, Math.floor(recoveryProgress * 100))
    : 100;
  const daysUntilNext = remaining < total && refillDaysPerToken > 0
    ? Math.max(1, Math.ceil((1 - recoveryProgress) * refillDaysPerToken))
    : 0;

  return (
    <div className="rest-days-section">
      <div className="rest-days-header">
        <h4 className="rest-days-title"><span aria-hidden="true">🌴</span> {dojoContext ? 'Your Rest Days' : 'Rest Days'}</h4>
        <div className="rest-days-summary">
          {allowanceEnabled ? (
            <>
              <span className="rest-days-remaining">{remaining}</span>
              <span className="rest-days-total"> / {total} available</span>
            </>
          ) : (
            <span className="rest-days-total">Disabled</span>
          )}
        </div>
      </div>

      {(dojoContext || isDojoOwned) && (
        <div className="rest-days-personal-scope">
          {isDojoOwned
            ? <>Allowance established by {allowanceOwner} · your remaining balance and recovery are personal to you.</>
            : 'Personal to you · changing this allowance does not change the dojo plan.'}
        </div>
      )}

      <p className="rest-days-help">
        {isDojoOwned && !allowanceEnabled
          ? `${allowanceOwner} has not enabled Rest Days.`
          : <>Use a rest day from a workout when you need a break or you're recovering. {total > 0
            ? <>You regain about one day every {refillCadenceDays} day{refillCadenceDays === 1 ? '' : 's'} ({total} per year), capped at {total}.</>
            : 'Set an annual allowance to start recharging rest days.'}</>}
      </p>

      {allowanceEnabled && remaining < total && (
        <div className="rest-days-recovery">
          <div className="rest-days-recovery-heading">
            <span>Next rest day</span>
            <strong>{recoveryPercent}% recharged</strong>
          </div>
          <div
            className="rest-days-recovery-progress"
            role="progressbar"
            aria-label="Progress toward the next available rest day"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={recoveryPercent}
            aria-valuetext={`${recoveryPercent} percent restored; about ${daysUntilNext} days until the next rest day`}
          >
            <span style={{ width: `${recoveryPercent}%` }} />
          </div>
          <small>About {daysUntilNext} day{daysUntilNext === 1 ? '' : 's'} until one full day is available.</small>
        </div>
      )}

      {(allowanceEnabled || canEditAllowance) && (!editing || !canEditAllowance ? (
        <div className="rest-days-row">
          {allowanceEnabled && (
            <span className="rest-days-used">
              {used === 0 ? 'Fully recharged' : `${used} rest day${used === 1 ? '' : 's'} currently unavailable`}
            </span>
          )}
          {canEditAllowance && (
            <button
              type="button"
              className="rest-days-btn"
              onClick={() => setEditing(true)}
            >
              {isDojoOwned ? 'Change allowance' : 'Change quota'}
            </button>
          )}
        </div>
      ) : (
        <div className="rest-days-edit">
          <label className="rest-days-label" htmlFor="rest-days-quota">
            Rest days per year
          </label>
          <div className="rest-days-edit-row">
            <input
              id="rest-days-quota"
              type="number"
              min="0"
              max="365"
              value={draftQuota}
              onChange={(e) => setDraftQuota(e.target.value)}
              className="rest-days-input"
              disabled={saving}
            />
            <button
              type="button"
              className="rest-days-btn primary"
              onClick={save}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="rest-days-btn"
              onClick={() => {
                setDraftQuota(String(total));
                setEditing(false);
                setError(null);
              }}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      ))}

      {error && <div className="rest-days-error" role="alert">{error}</div>}
    </div>
  );
};

export default RestDaysSettings;
